import i18n from "@/i18n";
/** WORKFORCE-EXTERNAL-EXTRAS-QR-V1 — restaurant QR card (view/print/rotate/revoke). */
import { useCallback, useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Printer, RefreshCw, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
    AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { extraErrorMessage, extraFormUrl } from "./externalExtrasHelpers";

const CONFIRM = {
    ROTATE: ["Replace the restaurant QR?", "All previously printed copies of the current code stop working immediately. Print and post the new code."],
    REVOKE: ["Deactivate the restaurant QR?", "All previously printed copies stop working immediately. Extras cannot report shifts until you generate a new code."],
};

export const PRINT_HEADING = "External staff — report your shift";
export const PRINT_STEPS = [
    "Scan this code with your phone camera.",
    "Enter your name and the agency or company you work for.",
    "Report your shift start and end times.",
    "Answer whether you ate a staff meal and whether you finished at the expected time.",
];

const esc = (t) => String(t).replace(/&/g, "&amp;").replace(/</g, "&lt;");

export function printPageHtml(svgMarkup, restaurantName, url) {
    return `<html><head><title>${esc(restaurantName)} · shift report QR</title></head>
<body style="font-family:sans-serif;text-align:center;padding:32px;color:#111">
<h1 style="margin:0 0 6px;font-size:32px">${esc(restaurantName)}</h1>
<h2 style="margin:0 0 20px;font-size:24px;font-weight:600">${i18n.t("wf.ExternalExtrasQrCard.PRINT_HEADING", PRINT_HEADING)}</h2>
<div>${svgMarkup}</div>
<ol style="text-align:left;display:inline-block;font-size:17px;line-height:1.6;margin:24px auto 8px">
${PRINT_STEPS.map((x) => `<li>${esc(x)}</li>`).join("")}</ol>
<p style="font-size:17px;font-weight:600;margin:8px 0">{i18n.t("wf.externalExtrasQrCard.noPinOrAccountNeeded", "No PIN or account needed.")}</p>
<p style="font-size:11px;color:#555;margin-top:16px;word-break:break-all">${esc(url)}</p></body></html>`;
}

function printQr(svgEl, restaurantName, url) {
    const w = window.open("", "_blank", "width=700,height=1000");
    if (!w) return;
    w.document.write(printPageHtml(svgEl.outerHTML, restaurantName, url));
    w.document.close();
    w.focus();
    w.print();
}

export function ExternalExtrasQrCard({ restaurantId, restaurantName, canManage = true }) {
    const [qr, setQr] = useState(null);
    const [confirm, setConfirm] = useState(null);
    const ref = useRef(null);
    const printRef = useRef(null);
    const load = useCallback(() => {
        api.get("/workforce/external-extras/qr", { params: { restaurant_id: restaurantId } })
            .then(({ data }) => setQr(data)).catch((e) => toast.error(extraErrorMessage(e, "Could not load the QR code.")));
    }, [restaurantId]);
    useEffect(() => { if (restaurantId) load(); }, [restaurantId, load]);

    const act = async (action) => {
        setConfirm(null);
        try {
            const { data } = await api.post("/workforce/external-extras/qr", { restaurant_id: restaurantId, action });
            setQr(data);
            toast.success(action === "ROTATE" ? i18n.t("wf.externalExtrasQrCard.restaurantQrReady", "Restaurant QR ready") : i18n.t("wf.externalExtrasQrCard.restaurantQrDeactivated", "Restaurant QR deactivated"));
        } catch (e) {
            toast.error(extraErrorMessage(e, "Could not update the QR code."));
        }
    };
    const url = extraFormUrl(window.location.origin, qr?.token);
    const active = qr?.status === "ACTIVE";
    return (
        <section className="rounded-xl border border-neutral-200 bg-white p-5" data-testid="extras-qr-card">
            <h2 className="text-base font-semibold text-neutral-900" data-testid="extras-qr-title">{i18n.t("wf.externalExtrasQrCard.restaurantQr", "Restaurant QR")}</h2>
            <p className="mt-1 text-sm text-neutral-600">
                {i18n.t("wf.externalExtrasQrCard.agencyExtrasScanThisCodeTo", "Agency extras scan this code to report their shift. No account or PIN. Reports wait for your review and never go to payroll.")}
            </p>
            {active ? (
                <div className="mt-4 flex flex-wrap items-center gap-6">
                    <div ref={ref} className="rounded-lg border border-neutral-200 p-3" data-testid="extras-qr-code">
                        <QRCodeSVG value={url} size={176} level="M" />
                    </div>
                    <div ref={printRef} className="hidden" aria-hidden="true">
                        <QRCodeSVG value={url} size={420} level="M" includeMargin />
                    </div>
                    <div className="space-y-2 text-sm">
                        <p className="break-all text-neutral-600" data-testid="extras-qr-url">{url}</p>
                        <p className="text-neutral-500">{i18n.t("wf.externalExtrasQrCard.validUntilYouCreateANew", "Valid until you create a new code or turn it off.")}</p>
                        <div className="flex flex-wrap gap-2">
                            <Button size="sm" data-testid="extras-qr-print"
                                onClick={() => printQr(printRef.current.querySelector("svg"), restaurantName || "", url)}>
                                <Printer className="mr-1.5 h-4 w-4" />Print
                            </Button>
                            {canManage && (
                                <>
                                    <Button size="sm" variant="outline" data-testid="extras-qr-rotate" onClick={() => setConfirm("ROTATE")}>
                                        <RefreshCw className="mr-1.5 h-4 w-4" />Replace QR
                                    </Button>
                                    <Button size="sm" variant="outline" data-testid="extras-qr-revoke" onClick={() => setConfirm("REVOKE")}>
                                        <ShieldOff className="mr-1.5 h-4 w-4" />Deactivate
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="mt-4 flex items-center gap-3">
                    <p className="text-sm text-neutral-600" data-testid="extras-qr-status">
                        {qr?.status === "NONE" || !qr ? i18n.t("wf.externalExtrasQrCard.noRestaurantQrYet", "No restaurant QR yet.") : i18n.t("wf.externalExtrasQrCard.theRestaurantQrIsDeactivatedPrinted", "The restaurant QR is deactivated. Printed copies no longer work.")}
                    </p>
                    {canManage && (
                        <Button size="sm" data-testid="extras-qr-create" onClick={() => act("ROTATE")}>{i18n.t("wf.externalExtrasQrCard.generateRestaurantQr", "Generate restaurant QR")}</Button>
                    )}
                </div>
            )}
            <AlertDialog open={Boolean(confirm)} onOpenChange={(o) => !o && setConfirm(null)}>
                <AlertDialogContent data-testid="extras-qr-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{confirm && CONFIRM[confirm][0]}</AlertDialogTitle>
                        <AlertDialogDescription>{confirm && CONFIRM[confirm][1]}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="extras-qr-confirm-cancel">{i18n.t("common.cancel", "Cancel")}</AlertDialogCancel>
                        <AlertDialogAction data-testid="extras-qr-confirm-ok" onClick={() => act(confirm)}>
                            {i18n.t("wf.externalExtrasQrCard.continue", "Continue")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </section>
    );
}
